import React, { useState, useCallback, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import {
  FaChartBar, FaDownload, FaCalendarAlt, FaGasPump,
  FaArrowUp, FaArrowDown, FaWrench,
  FaTruck, FaUser, FaHelicopter, FaTachometerAlt, FaLayerGroup,
  FaDatabase, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaCaretDown
} from 'react-icons/fa';
import { useApiCall } from '../hooks/useApiCall';
import { useToast } from '../components/Toast';
import { useModal } from '../components/Modal';
import '../styles/Statistics.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toTs(date) {
  return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).getTime() / 1000);
}
function toEndTs(date) {
  return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime() / 1000);
}

function today() {
  const d = new Date(); d.setHours(0,0,0,0); return d;
}
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n); d.setHours(0,0,0,0); return d;
}
function fmtDateStr(date) {
  return date.toISOString().slice(0, 10);
}

function fmtNum(n) {
  return n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function exportCSV(rows, from, to) {
  const headers = [
    'ID','Type','Date','Time',
    'Amount','Unit','Fuel Type',
    'Tanker','Aircraft','Fueler','Vendor',
    'Meter Before','Meter After','Meter Unit',
    'Photo Before URL','Photo After URL','Photo Receipt URL',
    'Status','Notes'
  ];
  const escape  = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines   = [
    headers.join(','),
    ...rows.map(r => [
      r.id, r.type, r.date, r.time,
      r.amount, r.amount_type, r.fuel_type,
      r.tanker, r.aircraft, r.fueler, r.vendor,
      r.meter_before, r.meter_after, r.meter_unit,
      r.photo_before, r.photo_after, r.photo_receipt,
      r.status, r.notes
    ].map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `UAS-Fueling-Report-${from}-to-${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportAircraftCSV(rows, from, to) {
  const headers = ['Registration','Fuel Type','Ops','Total Fuel','Unit','Avg / Op','Last Fueled'];
  const escape  = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines   = [
    headers.join(','),
    ...rows.map(r => [
      r.aircraft_reg, r.fuel_type, r.ops_count, r.total_fuel, r.amount_type,
      r.avg_per_op, new Date(r.last_fueled * 1000).toLocaleDateString()
    ].map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `UAS-Fleet-Consumption-${from}-to-${to}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportTankersCSV(rows, from, to) {
  const headers = ['Tanker','Unit','Dispensed','Restocked','Transactions'];
  const escape  = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines   = [
    headers.join(','),
    ...rows.map(r => [
      r.tanker_name, r.amount_type, r.out_volume, r.in_volume, r.txn_count
    ].map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `UAS-Tankers-${from}-to-${to}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportFuelersCSV(rows, from, to) {
  const headers = ['Fueler','Dispensed (L)','Restocked (L)','Total Transactions'];
  const escape  = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines   = [
    headers.join(','),
    ...rows.map(r => [
      r.fueler_name, r.out_volume, r.in_volume, r.txn_count
    ].map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `UAS-Fuelers-${from}-to-${to}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportFuelTypesCSV(rows, from, to) {
  const headers = ['Fuel Type','Unit','Dispensed','Restocked','Transactions'];
  const escape  = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines   = [
    headers.join(','),
    ...rows.map(r => [
      r.gas_type, r.amount_type, r.out_vol, r.in_vol, r.txn_count
    ].map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `UAS-FuelTypes-${from}-to-${to}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ── XLSX helpers ────────────────────────────────────────────────────────────

function downloadXLSX(sheetData, sheetName, filename) {
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

function exportXLSX(rows, from, to) {
  const headers = ['ID','Type','Date','Time','Amount','Unit','Fuel Type','Tanker','Aircraft','Fueler','Vendor','Meter Before','Meter After','Meter Unit','Photo Before URL','Photo After URL','Photo Receipt URL','Status','Notes'];
  const data = [headers, ...rows.map(r => [r.id,r.type,r.date,r.time,r.amount,r.amount_type,r.fuel_type,r.tanker,r.aircraft,r.fueler,r.vendor,r.meter_before,r.meter_after,r.meter_unit,r.photo_before,r.photo_after,r.photo_receipt,r.status,r.notes])];
  downloadXLSX(data, 'Transactions', `UAS-Fueling-Report-${from}-to-${to}.xlsx`);
}

function exportAircraftXLSX(rows, from, to) {
  const headers = ['Registration','Fuel Type','Ops','Total Fuel','Unit','Avg / Op','Last Fueled'];
  const data = [headers, ...rows.map(r => [r.aircraft_reg,r.fuel_type,r.ops_count,r.total_fuel,r.amount_type,r.avg_per_op,new Date(r.last_fueled * 1000).toLocaleDateString()])];
  downloadXLSX(data, 'Fleet Consumption', `UAS-Fleet-Consumption-${from}-to-${to}.xlsx`);
}

function exportTankersXLSX(rows, from, to) {
  const headers = ['Tanker','Unit','Dispensed','Restocked','Transactions'];
  const data = [headers, ...rows.map(r => [r.tanker_name,r.amount_type,r.out_volume,r.in_volume,r.txn_count])];
  downloadXLSX(data, 'Tankers', `UAS-Tankers-${from}-to-${to}.xlsx`);
}

function exportFuelersXLSX(rows, from, to) {
  const headers = ['Fueler','Dispensed (L)','Restocked (L)','Total Transactions'];
  const data = [headers, ...rows.map(r => [r.fueler_name,r.out_volume,r.in_volume,r.txn_count])];
  downloadXLSX(data, 'Fuelers', `UAS-Fuelers-${from}-to-${to}.xlsx`);
}

function exportFuelTypesXLSX(rows, from, to) {
  const headers = ['Fuel Type','Unit','Dispensed','Restocked','Transactions'];
  const data = [headers, ...rows.map(r => [r.gas_type,r.amount_type,r.out_vol,r.in_vol,r.txn_count])];
  downloadXLSX(data, 'Fuel Types', `UAS-FuelTypes-${from}-to-${to}.xlsx`);
}

// ── Export dropdown ──────────────────────────────────────────────────────────

function ExportDropdown({ onCSV, onXLSX }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="export-dropdown" ref={ref}>
      <button className="btn btn-primary export-dropdown-btn" onClick={() => setOpen(o => !o)}>
        <FaDownload /> Export <FaCaretDown className="export-caret" />
      </button>
      {open && (
        <div className="export-dropdown-menu">
          <button className="export-dropdown-item" onClick={() => { onCSV(); setOpen(false); }}>
            <span className="export-format-badge csv">CSV</span> Comma-separated
          </button>
          <button className="export-dropdown-item" onClick={() => { onXLSX(); setOpen(false); }}>
            <span className="export-format-badge xlsx">XLSX</span> Excel workbook
          </button>
        </div>
      )}
    </div>
  );
}

// ── Custom date input ───────────────────────────────────────────────────────
const DateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="stat-date-input" onClick={onClick} ref={ref}>
    <FaCalendarAlt className="stat-date-input-icon" />
    <span>{value}</span>
  </button>
));
DateInput.displayName = 'DateInput';

// ── Bar chart (pure CSS) ─────────────────────────────────────────────────────

function DailyChart({ daily }) {
  if (!daily?.length) return <div className="stat-empty">No data for this range.</div>;
  const maxOut = Math.max(...daily.map(d => Math.max(d.out_volume, d.in_volume)), 1);
  return (
    <div className="daily-chart">
      {daily.map(d => (
        <div key={d.day} className="daily-bar-col">
          <div className="daily-bar-wrap">
            {d.in_volume > 0 && (
              <div
                className="daily-bar daily-bar-in"
                style={{ height: `${Math.round((d.in_volume / maxOut) * 100)}%` }}
                title={`IN: ${fmtNum(d.in_volume)}`}
              />
            )}
            <div
              className="daily-bar daily-bar-out"
              style={{ height: `${Math.round((d.out_volume / maxOut) * 100)}%` }}
              title={`OUT: ${fmtNum(d.out_volume)}`}
            />
          </div>
          <div className="daily-bar-label">{d.day.slice(5)}</div>
        </div>
      ))}
      <div className="daily-chart-legend">
        <span className="dcl-dot out" /> Dispensed (OUT)
        <span className="dcl-dot in" /> Restocked (IN)
      </div>
    </div>
  );
}

// ── Stock gauge bar ──────────────────────────────────────────────────────────

function StockGauge({ item }) {
  const pct  = item.amount_cap > 0 ? Math.min(100, Math.round((item.amount / item.amount_cap) * 100)) : 0;
  const color = pct >= 60 ? '#35c068' : pct >= 25 ? '#f5af18' : '#db3131';
  const StatusIcon = pct >= 60 ? FaCheckCircle : pct >= 25 ? FaExclamationTriangle : FaTimesCircle;
  return (
    <div className="stat-stock-item">
      <div className="stat-stock-header">
        <div className="stat-stock-name">
          <StatusIcon style={{ color, fontSize: 13, flexShrink: 0 }} />
          <span>{item.name}</span>
          <span className="stat-stock-fueltype">{item.gas_type}</span>
        </div>
        <div className="stat-stock-pct" style={{ color }}>{pct}%</div>
      </div>
      <div className="stat-stock-bar-bg">
        <div className="stat-stock-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="stat-stock-nums">
        <span>{fmtNum(item.amount)} {item.amount_type}</span>
        <span style={{ color: 'var(--text-muted)' }}>/ {fmtNum(item.amount_cap)} {item.amount_type}</span>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function Statistics() {
  const apiCall  = useApiCall();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();

  const [fromDate, setFromDate] = useState(daysAgo(29));
  const [toDate,   setToDate]   = useState(today());
  const [activePreset, setActivePreset] = useState(30);
  const [tankerFilter, setTankerFilter] = useState(null); // null = all tankers
  const [tankerList,   setTankerList]   = useState([]);   // [{id, name}]
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [loaded,   setLoaded]   = useState(false);

  const handleLoad = useCallback(() => {
    if (!fromDate || !toDate) return;
    if (fromDate > toDate) return;
    setLoading(true);
    const body = { from_ts: toTs(fromDate), to_ts: toEndTs(toDate) };
    if (tankerFilter) body.tanker_id = tankerFilter;
    apiCall('admin/statistics', 'POST', body).then(res => {
      if (res?.success) {
        setData(res.data);
        setLoaded(true);
        // Populate tanker list on first (unfiltered) load
        if (!tankerFilter && res.data.current_stock?.length) {
          setTankerList(res.data.current_stock.map(s => ({ id: s.id, name: s.name })));
        }
      } else {
        showToast(res?.response || 'Failed to load statistics', 'error');
      }
      setLoading(false);
    });
  }, [fromDate, toDate, tankerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { handleLoad(); }, [fromDate, toDate, tankerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPreset = (days) => {
    setActivePreset(days);
    setFromDate(daysAgo(days - 1));
    setToDate(today());
  };

  const handleExportClick = useCallback((exportData, from, to) => {
    const id = openModal(
      'Export CSV',
      <p style={{ marginBottom: 4, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
        Download <strong style={{ color: 'var(--text-primary)' }}>{exportData.length} rows</strong> as a CSV file for <strong style={{ color: 'var(--text-primary)' }}>{from}</strong> → <strong style={{ color: 'var(--text-primary)' }}>{to}</strong>?
      </p>,
      'sm',
      {
        footer: (closeModal) => (
          <>
            <button className="btn btn-secondary" onClick={() => closeModal(id)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { exportCSV(exportData, from, to); closeModal(id); }}>
              <FaDownload /> Download
            </button>
          </>
        )
      }
    );
  }, [openModal, closeModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayDate = today();

  const s = data?.summary;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Statistics</h1>
          <p className="page-subtitle">Fuel activity reports &amp; CSV export by date range</p>
        </div>
        {loaded && data?.export?.length > 0 && (
          <ExportDropdown
            onCSV={() => exportCSV(data.export, fmtDateStr(fromDate), fmtDateStr(toDate))}
            onXLSX={() => exportXLSX(data.export, fmtDateStr(fromDate), fmtDateStr(toDate))}
          />
        )}
      </div>

      {/* ── Tanker filter ── */}
      {tankerList.length > 0 && (
        <div className="stat-tanker-filter">
          <FaTruck style={{ color: tankerFilter ? '#4da8ff' : 'var(--text-muted)', fontSize: 13 }} />
          <label>Tanker:</label>
          <select
            className="stat-filter-select"
            value={tankerFilter ?? ''}
            onChange={e => setTankerFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All tankers</option>
            {tankerList.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {tankerFilter && (
            <button className="btn btn-sm btn-secondary" onClick={() => setTankerFilter(null)}>
              Clear
            </button>
          )}
        </div>
      )}

      {/* ── Range picker ── */}
      <div className="stat-range-card">
        <div className="stat-range-presets">
          {[7, 14, 30, 90].map(d => (
            <button key={d} className={`btn stat-preset-btn${activePreset === d ? ' stat-preset-active' : ''}`} onClick={() => setPreset(d)}>
              Last {d} days
            </button>
          ))}
        </div>
        <div className="stat-range-inputs">
          <div className="stat-range-field">
            <label>From</label>
            <div className="stat-date-wrap">
              <DatePicker
                selected={fromDate}
                onChange={date => { if (date) { setFromDate(date); setActivePreset(null); } }}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate}
                dateFormat="MM/dd/yyyy"
                customInput={<DateInput />}
                popperClassName="stat-datepicker-popper"
                calendarClassName="stat-datepicker"
                popperProps={{ strategy: 'fixed' }}
              />
            </div>
          </div>
          <div className="stat-range-sep">-</div>
          <div className="stat-range-field">
            <label>To</label>
            <div className="stat-date-wrap">
              <DatePicker
                selected={toDate}
                onChange={date => { if (date) { setToDate(date); setActivePreset(null); } }}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                maxDate={todayDate}
                dateFormat="MM/dd/yyyy"
                customInput={<DateInput />}
                popperClassName="stat-datepicker-popper"
                calendarClassName="stat-datepicker"
                popperProps={{ strategy: 'fixed' }}
              />
            </div>
          </div>
        </div>
      </div>

      {!loaded && !loading && (
        <div className="stat-empty-state">
          <FaChartBar style={{ fontSize: 40, color: '#3a4048' }} />
          <div>Select a date range to view statistics.</div>
        </div>
      )}

      {loading && (
        <div className="stat-empty-state">
          <div className="stat-spinner" />
          <div>Loading statistics…</div>
        </div>
      )}

      {loaded && data && (
        <>
          {/* ── KPI Summary cards ── */}
          <div className="stat-summary-row">
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(219,49,49,0.12)', color: '#db3131' }}><FaArrowUp /></div>
              <div>
                <div className="stat-summary-value">{fmtNum(s.total_out)} <span className="stat-unit">L</span></div>
                <div className="stat-summary-label">Total dispensed (OUT)</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(53,192,104,0.12)', color: '#35c068' }}><FaArrowDown /></div>
              <div>
                <div className="stat-summary-value">{fmtNum(s.total_in)} <span className="stat-unit">L</span></div>
                <div className="stat-summary-label">Total restocked (IN)</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(77,168,255,0.12)', color: '#4da8ff' }}><FaHelicopter /></div>
              <div>
                <div className="stat-summary-value">{s.out_count}</div>
                <div className="stat-summary-label">Flight fueling ops</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(245,175,24,0.12)', color: '#f5af18' }}><FaTachometerAlt /></div>
              <div>
                <div className="stat-summary-value">{fmtNum(s.avg_per_flight)} <span className="stat-unit">L</span></div>
                <div className="stat-summary-label">Avg fuel per flight</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}><FaLayerGroup /></div>
              <div>
                <div className="stat-summary-value">{s.fleet_served}</div>
                <div className="stat-summary-label">Aircraft served</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}><FaGasPump /></div>
              <div>
                <div className="stat-summary-value">{s.in_count}</div>
                <div className="stat-summary-label">Restock ops</div>
              </div>
            </div>
            <div className="stat-summary-card">
              <div className="stat-summary-icon" style={{ background: 'rgba(156,163,175,0.12)', color: '#9ca3af' }}><FaWrench /></div>
              <div>
                <div className="stat-summary-value">{s.correction_count}</div>
                <div className="stat-summary-label">Corrections</div>
              </div>
            </div>
          </div>

          {/* ── Current stock levels ── */}
          {(() => {
            const visibleStock = tankerFilter
              ? data.current_stock?.filter(s => s.id === tankerFilter)
              : data.current_stock;
            return visibleStock?.length > 0 ? (
              <div className="stat-card">
                <div className="stat-card-title"><FaDatabase /> Current Tank Inventory</div>
                <div className="stat-stock-grid">
                  {visibleStock.map((item, i) => <StockGauge key={i} item={item} />)}
                </div>
              </div>
            ) : null;
          })()}

          {/* ── Daily chart ── */}
          <div className="stat-card">
            <div className="stat-card-title"><FaChartBar /> Daily Activity — {fmtDateStr(fromDate)} → {fmtDateStr(toDate)}</div>
            <DailyChart daily={data.daily} />
          </div>

          {/* ── Aircraft consumption ── */}
          <div className="stat-card">
            <div className="stat-card-title" style={{ justifyContent: 'space-between' }}>
              <span><FaHelicopter /> Fleet Fuel Consumption</span>
              {data.by_aircraft?.length > 0 && (
                <ExportDropdown
                  onCSV={() => exportAircraftCSV(data.by_aircraft, fmtDateStr(fromDate), fmtDateStr(toDate))}
                  onXLSX={() => exportAircraftXLSX(data.by_aircraft, fmtDateStr(fromDate), fmtDateStr(toDate))}
                />
              )}
            </div>
            {!data.by_aircraft?.length ? (
              <div className="stat-empty">No aircraft fueling data for this range.</div>
            ) : (
              <table className="stat-table">
                <thead>
                  <tr>
                    <th>Registration</th>
                    <th>Fuel Type</th>
                    <th>Ops</th>
                    <th>Total Fuel</th>
                    <th>Avg / Op</th>
                    <th>Last Fueled</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_aircraft.map((ac, i) => (
                    <tr key={i}>
                      <td><strong>{ac.aircraft_reg}</strong></td>
                      <td><span className="stat-fuel-badge">{ac.fuel_type || '—'}</span></td>
                      <td>{ac.ops_count}</td>
                      <td className="stat-red">{fmtNum(ac.total_fuel)} {ac.amount_type}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{fmtNum(ac.avg_per_op)} {ac.amount_type}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(ac.last_fueled * 1000).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Fuel type + Tanker + Fueler ── */}
          <div className="stat-three-col">
            <div className="stat-card">
              <div className="stat-card-title" style={{ justifyContent: 'space-between' }}>
                <span><FaGasPump /> By Fuel Type</span>
                {data.by_fuel_type?.length > 0 && (
                  <ExportDropdown
                    onCSV={() => exportFuelTypesCSV(data.by_fuel_type, fmtDateStr(fromDate), fmtDateStr(toDate))}
                    onXLSX={() => exportFuelTypesXLSX(data.by_fuel_type, fmtDateStr(fromDate), fmtDateStr(toDate))}
                  />
                )}
              </div>
              {!data.by_fuel_type?.length ? (
                <div className="stat-empty">No data.</div>
              ) : (
                <table className="stat-table">
                  <thead><tr><th>Fuel</th><th>Dispensed</th><th>Restocked</th></tr></thead>
                  <tbody>
                    {data.by_fuel_type.map((ft, i) => (
                      <tr key={i}>
                        <td><span className="stat-fuel-badge">{ft.gas_type}</span></td>
                        <td className="stat-red">{fmtNum(ft.out_vol)} {ft.amount_type}</td>
                        <td className="stat-green">{fmtNum(ft.in_vol)} {ft.amount_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {!tankerFilter && (
            <div className="stat-card">
              <div className="stat-card-title" style={{ justifyContent: 'space-between' }}>
                <span><FaTruck /> By Tanker</span>
                {data.by_tanker?.length > 0 && (
                  <ExportDropdown
                    onCSV={() => exportTankersCSV(data.by_tanker, fmtDateStr(fromDate), fmtDateStr(toDate))}
                    onXLSX={() => exportTankersXLSX(data.by_tanker, fmtDateStr(fromDate), fmtDateStr(toDate))}
                  />
                )}
              </div>
              {!data.by_tanker?.length ? (
                <div className="stat-empty">No data.</div>
              ) : (
                <table className="stat-table">
                  <thead><tr><th>Tanker</th><th>Dispensed</th><th>Restocked</th><th>Txns</th></tr></thead>
                  <tbody>
                    {data.by_tanker.map((t, i) => (
                      <tr key={i}>
                        <td>{t.tanker_name}</td>
                        <td className="stat-red">{fmtNum(t.out_volume)} {t.amount_type}</td>
                        <td className="stat-green">{fmtNum(t.in_volume)} {t.amount_type}</td>
                        <td>{t.txn_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )}

            <div className="stat-card">
              <div className="stat-card-title" style={{ justifyContent: 'space-between' }}>
                <span><FaUser /> Top Fuelers</span>
                {data.by_fueler?.length > 0 && (
                  <ExportDropdown
                    onCSV={() => exportFuelersCSV(data.by_fueler, fmtDateStr(fromDate), fmtDateStr(toDate))}
                    onXLSX={() => exportFuelersXLSX(data.by_fueler, fmtDateStr(fromDate), fmtDateStr(toDate))}
                  />
                )}
              </div>
              {!data.by_fueler?.length ? (
                <div className="stat-empty">No data.</div>
              ) : (
                <table className="stat-table">
                  <thead><tr><th>Fueler</th><th>Dispensed</th><th>IN ops</th><th>Total txns</th></tr></thead>
                  <tbody>
                    {data.by_fueler.map((f, i) => (
                      <tr key={i}>
                        <td>{f.fueler_name}</td>
                        <td className="stat-red">{fmtNum(f.out_volume)} L</td>
                        <td className="stat-green">{fmtNum(f.in_volume)} L</td>
                        <td>{f.txn_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Export preview ── */}
          {data.export.length > 0 && (
            <div className="stat-card">
              <div className="stat-card-title" style={{ justifyContent: 'space-between' }}>
                <span><FaDownload /> Transaction Log <span style={{ fontWeight: 400, color: '#8896a5', fontSize: 12 }}>({data.export.length} rows)</span></span>
                <ExportDropdown
                  onCSV={() => exportCSV(data.export, fmtDateStr(fromDate), fmtDateStr(toDate))}
                  onXLSX={() => exportXLSX(data.export, fmtDateStr(fromDate), fmtDateStr(toDate))}
                />
              </div>
              <div className="stat-export-wrap">
                <table className="stat-table stat-export-table">
                  <thead>
                    <tr><th>#</th><th>Type</th><th>Amount</th><th>Fuel</th><th>Aircraft</th><th>Tanker</th><th>Fueler</th><th>Meter Before→After</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {data.export.slice(0, 50).map(r => (
                      <tr key={r.id}>
                        <td style={{ color: '#8896a5' }}>{r.id}</td>
                        <td>
                          <span className={`txn-type-badge ${r.type.toLowerCase()}`}>
                            {r.type === 'OUT' ? <FaArrowUp /> : r.type === 'IN' ? <FaArrowDown /> : <FaWrench />} {r.type}
                          </span>
                        </td>
                        <td>{fmtNum(r.amount)} {r.amount_type}</td>
                        <td><span className="stat-fuel-badge">{r.fuel_type || '—'}</span></td>
                        <td><strong>{r.aircraft || '—'}</strong></td>
                        <td>{r.tanker || '—'}</td>
                        <td>{r.fueler || '—'}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {r.meter_before !== '' ? `${fmtNum(r.meter_before)} → ${fmtNum(r.meter_after)} ${r.meter_unit}` : '—'}
                        </td>
                        <td style={{ color: '#8896a5', fontSize: 12 }}>{r.date} {r.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.export.length > 50 && (
                  <div className="stat-export-more">
                    + {data.export.length - 50} more rows — download the CSV for the full report
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
